import i18n from "../i18n";

const ATTENDANCE_TYPE_MAP = {
  clock_in: { short: "entree", label: "ENTREE" },
  clock_out: { short: "sortie", label: "SORTIE" },
  break_start: { short: "debut_pause", label: "DEBUT_PAUSE" },
  break_end: { short: "fin_pause", label: "FIN_PAUSE" },
  overtime_start: { short: "debut_hs", label: "DEBUT_HEURES_SUP" },
  overtime_end: { short: "fin_hs", label: "FIN_HEURES_SUP" },
  absence: { short: "absence", label: "ABSENCE" },
};

const BACKEND_STATUS_TO_UI = {
  pending: "EN_ATTENTE",
  approved_manager: "VALIDE_CHEF",
  approved_hr: "VALIDE_RH",
  rejected: "REJETE",
  exported_oracle: "EXPORTE_ORACLE",
};

const UI_STATUS_TO_BACKEND = {
  EN_ATTENTE: "pending",
  EN_ATTENTE_CHEF: "pending",
  VALIDE_CHEF: "approved_manager",
  VALIDE_RH: "approved_hr",
  REJETE: "rejected",
  EXPORTE_ORACLE: "exported_oracle",
};

const ANOMALY_TYPE_MAP = {
  late: "RETARD",
  early_departure: "DEPART_ANTICIPE",
  missing_clock: "OUBLI_POINTAGE",
  abnormal_duration: "DUREE_ANORMALE",
  duplicate_clock: "DOUBLE_POINTAGE",
};

export function mapAttendanceToPointage(attendance) {
  if (!attendance || typeof attendance !== "object") {
    return null;
  }

  const typeConfig =
    ATTENDANCE_TYPE_MAP[attendance.attendance_type] || {
      short: attendance.attendance_type || "autre",
      label: (attendance.attendance_type || "AUTRE").toUpperCase(),
    };

  // Handle both processed attendances (status is string) and raw logs (status is number, use processed field)
  let statut;
  if (typeof attendance.status === "number") {
    // Raw log: use 'processed' field instead of 'status'
    statut = BACKEND_STATUS_TO_UI[attendance.processed] || (attendance.processed || "EN_ATTENTE").toUpperCase();
  } else {
    // Processed attendance: use 'status' field
    statut = BACKEND_STATUS_TO_UI[attendance.status] || (attendance.status || "").toUpperCase();
  }

  const workedHours = attendance.worked_hours != null ? Number(attendance.worked_hours) : null;
  const overtimeHours = attendance.overtime_hours != null ? Number(attendance.overtime_hours) : 0;

  return {
    id: attendance.id,
    employee_id: attendance.employee_id,
    employee_name: attendance.employee_name || attendance.registration_number || i18n.t("common.employeeWithId", { id: attendance.employee_id }),
    registration_number: attendance.registration_number,
    timestamp: attendance.timestamp,
    type: typeConfig.short,
    type_pointage: typeConfig.label,
    statut,
    statut_backend: typeof attendance.status === "number" ? attendance.processed : attendance.status,
    anomalie: Boolean(attendance.has_anomaly),
    type_anomalie: attendance.anomaly_type ? ANOMALY_TYPE_MAP[attendance.anomaly_type] || attendance.anomaly_type.toUpperCase() : null,
    description_anomalie: attendance.anomaly_description || "",
    retard_minutes: attendance.late_minutes || 0,
    heures_travaillees: workedHours,
    heures_supplementaires: overtimeHours,
    exporte_oracle: Boolean(attendance.exported_to_oracle),
    oracle_reference: attendance.oracle_reference,
    latitude: attendance.latitude ?? null,
    longitude: attendance.longitude ?? null,
    terminal_id: attendance.terminal_id,
    terminal_name: attendance.terminal_name,
    device_id: attendance.terminal_name || attendance.terminal_id || "N/A",
    validated_by_manager_at: attendance.validated_by_manager_at,
    validated_by_hr_at: attendance.validated_by_hr_at,
    manager_validator_id: attendance.manager_validator_id,
    hr_validator_id: attendance.hr_validator_id,
    manager_comment: attendance.manager_comment,
    hr_comment: attendance.hr_comment,
    created_at: attendance.created_at,
    updated_at: attendance.updated_at,
    site_id: attendance.site_id,
    site_name: attendance.site_name,
    department_id: attendance.department_id,
    department_name: attendance.department_name,
    job_id: attendance.job_id,
    job_name: attendance.job_name,
    raw: attendance,
  };
}

export function mapUiStatusToBackend(statut) {
  if (!statut) return undefined;
  return UI_STATUS_TO_BACKEND[statut] || statut.toLowerCase();
}

export function mapUiTypeToBackend(type) {
  if (!type) return undefined;
  const entry = Object.entries(ATTENDANCE_TYPE_MAP).find(([, cfg]) => cfg.short === type);
  return entry ? entry[0] : type;
}

export function getDisplayLabelForStatus(statut) {
  switch (statut) {
    case "EN_ATTENTE":
      return i18n.t("attendanceMapper.status.pending");
    case "VALIDE_CHEF":
      return i18n.t("attendanceMapper.status.approvedManager");
    case "VALIDE_RH":
      return i18n.t("attendanceMapper.status.approvedHr");
    case "REJETE":
      return i18n.t("attendanceMapper.status.rejected");
    case "EXPORTE_ORACLE":
      return i18n.t("attendanceMapper.status.exportedOracle");
    default:
      return statut || i18n.t("common.unknownStatus");
  }
}

export const attendanceMapperConstants = {
  ATTENDANCE_TYPE_MAP,
  BACKEND_STATUS_TO_UI,
  UI_STATUS_TO_BACKEND,
  ANOMALY_TYPE_MAP,
};
